<template>
    <Treeselect ref="TreeSelect" v-bind="attrs" :class="{ 'is-valid': attrs.state === true, 'is-invalid': attrs.state === false }" v-on="listeners">
        <template v-for="slotName in Object.keys($slots)" #[slotName]>
            <slot :name="slotName"></slot>
        </template>
        <template v-for="slotName in Object.keys($scopedSlots)" #[slotName]="slotProps">
            <slot :name="slotName" v-bind="slotProps"></slot>
        </template>
    </Treeselect>
</template>



<script>
    import Treeselect from '@riophae/vue-treeselect';

    export default {
        name: 'FormSelectTree',
        inheritAttrs: false,
        components: {
            Treeselect,
        },
        props: {
            showCountValue: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                options: this.$attrs.options,
                originalRenderMultiValueItems: null,
                originalClear: null,
                openValue: null,
                closeValue: null,
            };
        },
        computed: {
            attrs() {
                const attrs = {
                    clearAllText: this.$t('clearAll'),
                    clearValueText: this.$t('clearValue'),
                    loadingText: this.$t('loadingDots'),
                    noChildrenText: this.$t('noSubOptions'),
                    noOptionsText: this.$t('noOptionsAvailable'),
                    noResultsText: this.$t('noResultsFound'),
                    placeholder: this.$t('selectDots'),
                    retryText: this.$t('retry'),
                    retryTitle: this.$t('clickToRetry'),
                    searchPromptText: this.$t('typeToSearch'),
                    ...this.$attrs,
                    options: this.options,
                };
                if (typeof this.$attrs['load-options'] === 'function') {
                    attrs['load-options'] = this.loadOptions;
                }
                return attrs;
            },
            listeners() {
                return {
                    ...this.$listeners,
                    open: this.open,
                    close: this.close,
                };
            },
            value() {
                if (!this.$refs.TreeSelect?.internalValue) return [];
                return this.$refs.TreeSelect.internalValue.slice(0, this.$refs.TreeSelect.limit).map(this.$refs.TreeSelect.getNode);
            },
        },
        watch: {
            '$attrs.options': {
                handler(options) {
                    this.options = options;
                },
            },
            showCountValue() {
                this.setRenderMultiValueItems();
            },
        },
        methods: {
            hasChanged(openValue, closeValue) {
                if (Array.isArray(openValue) && Array.isArray(closeValue)) {
                    const comparator = typeof openValue[0] === 'number' && typeof closeValue[0] === 'number' ? (a, b) => a - b : undefined;
                    const sortedOpen = [...openValue].sort(comparator);
                    const sortedClose = [...closeValue].sort(comparator);
                    return JSON.stringify(sortedOpen) !== JSON.stringify(sortedClose);
                }
                return openValue !== closeValue;
            },
            open(instanceId) {
                this.openValue = this.$refs.TreeSelect?.value || null;
                this.closeValue = null;
                this.$emit('open', this.openValue, instanceId);
            },
            close(value, instanceId) {
                this.closeValue = this.$refs.TreeSelect?.value || null;
                this.$emit('open', this.closeValue, instanceId);
                if (this.hasChanged(this.openValue, this.closeValue)) {
                    this.$emit('change', this.closeValue, instanceId);
                }
                this.openValue = null;
                this.closeValue = null;
            },
            async loadOptions(params) {
                const { action, callback, parentNode } = params;
                const internalCallback = data => {
                    if (data instanceof Error) {
                        return callback(data);
                    }
                    if (action === 'LOAD_ROOT_OPTIONS') {
                        this.options = data;
                        return callback();
                    }
                    if (action === 'LOAD_CHILDREN_OPTIONS') {
                        parentNode.children = data;
                        return callback();
                    }
                    if (action === 'ASYNC_SEARCH') {
                        return callback(null, data);
                    }
                    return callback();
                };
                this.emitLoading(action, true);
                return this.$attrs['load-options']({ ...params, callback: internalCallback })
                    .then(internalCallback)
                    .catch(internalCallback)
                    .finally(() => {
                        this.emitLoading(action, false);
                    });
            },
            emitLoading(action, value) {
                if (action === 'LOAD_ROOT_OPTIONS') {
                    this.$emit('rootLoading', value);
                }
                if (action === 'LOAD_CHILDREN_OPTIONS') {
                    this.$emit('childrenLoading', value);
                }
                if (action === 'ASYNC_SEARCH') {
                    this.$emit('searchLoading', value);
                }
            },
            addNode(parentId, newNode) {
                const findAndInsert = nodes => {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const node of nodes) {
                        if (node.id === parentId) {
                            if (!Array.isArray(node.children)) {
                                node.children = [];
                            }
                            node.children.push(newNode);
                            return newNode;
                        }
                        if (Array.isArray(node.children)) {
                            const found = findAndInsert(node.children);
                            if (found) {
                                return found;
                            }
                        }
                    }
                    return false;
                };
                const inserted = findAndInsert(this.options);
                return inserted;
            },
            removeNode(nodeId) {
                const findAndRemove = nodes => {
                    for (let i = 0; i < nodes.length; i++) {
                        // Jeśli to szukany węzeł, usuń go
                        if (nodes[i].id === nodeId) {
                            const removedNode = nodes.splice(i, 1);
                            return removedNode[0];
                        }
                        // W przeciwnym razie, jeśli węzeł ma dzieci, spróbuj usunąć w nich
                        if (Array.isArray(nodes[i].children)) {
                            const found = findAndRemove(nodes[i].children);
                            if (found) {
                                return found;
                            }
                        }
                    }
                    return false;
                };
                const removed = findAndRemove(this.options);
                return removed;
            },
            localRenderMultiValueItems() {
                const nodes = this.$refs.TreeSelect?.internalValue.slice(0, this.$refs.TreeSelect.limit).map(this.$refs.TreeSelect.getNode) || [];
                const label = nodes.length ? `${this.$t('selectField.selectedOptions')} (${nodes.length})` : '';
                const inputValue = this.$refs.TreeSelect?.$refs.control?.$refs['value-container']?.$refs.input?.value;
                return this.$createElement('div', { key: 'MultiValueItemsCount', class: { 'vue-treeselect__multi-value-items-count': true, 'vue-treeselect__is-value': inputValue, 'vue-treeselect__is-empty': !inputValue } }, label);
            },
            setRenderMultiValueItems() {
                const vNodeValue = this.$refs.TreeSelect?.$refs.control?.$refs['value-container'];
                if (vNodeValue) {
                    this.originalRenderMultiValueItems = vNodeValue.renderMultiValueItems || null;
                    const currentFn = this.showCountValue ? this.localRenderMultiValueItems : this.originalRenderMultiValueItems;
                    vNodeValue.renderMultiValueItems = currentFn;
                    vNodeValue.$forceUpdate();
                }
            },
            setClear() {
                const vNodeControl = this.$refs.TreeSelect?.$refs.control;
                if (vNodeControl) {
                    this.originalClear = vNodeControl.instance.clear || null;
                    vNodeControl.instance.clear = this.localClear;
                }
            },
            localClear() {
                this.$refs.TreeSelect.$on('input', this.emitClearEvent);
                this.originalClear.bind(this.$refs.TreeSelect.$refs.control.instance)();
            },
            emitClearEvent(value) {
                this.$emit('clear', value);
                this.$emit('change', value);
                this.$refs.TreeSelect.$off('input', this.emitClearEvent);
            },
        },
        mounted() {
            this.setRenderMultiValueItems();
            this.setClear();
        },
    };
</script>



<style lang="scss" scoped>
    @import 'bootstrap/scss/functions';
    @import 'bootstrap/scss/variables';
    @import 'bootstrap/scss/mixins';
    @import '@/design/custom/_variables.scss';

    .vue-treeselect {
        &.is-invalid {
            ::v-deep {
                .vue-treeselect__control {
                    border-color: $danger;
                }
            }
        }
        &.is-valid {
            ::v-deep {
                .vue-treeselect__control {
                    border-color: $success;
                }
            }
        }

        ::v-deep {
            &:not(.vue-treeselect--disabled) .vue-treeselect__control-arrow-container .vue-treeselect__control-arrow {
                color: #999 !important;
            }

            .vue-treeselect__control {
                position: relative;
                display: flex;
                gap: 10px;
                align-items: center;
                min-height: $input-height !important;
                padding: $input-padding-y 58px $input-padding-y $input-padding-x !important;
                font-family: $font-family-base !important;
                @include font-size($input-font-size);
                font-weight: $input-font-weight !important;
                line-height: $input-line-height !important;
                color: $input-color !important;
                background-color: $input-bg !important;
                background-clip: padding-box;
                border: $input-border-width solid $input-border-color;
            }

            .vue-treeselect__control-arrow-container,
            .vue-treeselect__x-container {
                line-height: 16px;
                display: block;
                position: absolute;
                box-sizing: border-box;
                width: 20px;
                height: 38px;
                right: 11px;
                top: 1px;
                padding: 4px;
                margin: 0;
                text-decoration: none;
                text-align: center;
                cursor: pointer;
                transition: transform 0.2s ease;
                display: flex;
                justify-content: center;
                align-items: center;
            }

            .vue-treeselect__x-container {
                right: 31px;
            }

            .vue-treeselect__value-container {
                display: flex;
            }

            .vue-treeselect__placeholder,
            .vue-treeselect__input-container,
            .vue-treeselect__input {
                padding: 0 !important;
                height: 22px;
                line-height: 22px;
            }

            .vue-treeselect__placeholder {
                color: $input-color !important;
                opacity: 0.52;
            }

            .vue-treeselect__multi-value,
            .vue-treeselect__single-value {
                display: flex;
                flex-wrap: nowrap;
                overflow: hidden;
                margin: 0;
                line-height: 22px;
                padding: 0;
            }

            .vue-treeselect__multi-value-item-container * {
                white-space: nowrap;
            }

            .vue-treeselect__control-arrow {
                transition: transform 0.3s ease;
            }

            .vue-treeselect__menu {
                overflow: auto;
                @include font-size($input-font-size);
            }

            .vue-treeselect__label {
                overflow: auto;
                text-overflow: unset;
            }

            .vue-treeselect__multi-value-items-count {
                white-space: nowrap;
                height: 22px;
                line-height: 22px;
                margin: auto 0;
                user-select: none;
            }
        }

        &.vue-treeselect--open {
            ::v-deep {
                .vue-treeselect__multi-value-items-count {
                    &.vue-treeselect__is-value {
                        display: none;
                    }
                    &.vue-treeselect__is-empty {
                        pointer-events: none;
                        position: absolute;
                        right: 0;
                        top: 0;
                        bottom: 0;
                        left: 0;
                        overflow: hidden;
                        color: $input-color !important;
                        opacity: 0.52;
                    }
                }
            }
        }
    }
</style>
